/**
 * Funciones de sanitización y transformación de datos
 * Limpian y formatean valores de entrada del usuario
 */

import { LIMITES } from './validationRules';

// ============================================================================
// SANITIZADORES DE TEXTO
// ============================================================================

/**
 * Sanitiza el nombre completo:
 * - Solo permite letras, acentos, ñ y espacios
 * - Convierte a mayúsculas
 * @param value Valor de entrada
 * @returns Valor sanitizado en mayúsculas
 */
export const sanitizeNombre = (value: string): string => {
  const cleanValue = value.replace(/[^A-ZÁÉÍÓÚÜÑa-záéíóúüñ\s]/g, '');
  return cleanValue.toUpperCase();
};

/**
 * Sanitiza el número de teléfono:
 * - Solo permite dígitos
 * - Limita a 10 dígitos
 * @param value Valor de entrada
 * @returns Valor sanitizado solo con dígitos
 */
export const sanitizeTelefono = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.slice(0, LIMITES.TELEFONO.DIGITOS);
};

// ============================================================================
// SANITIZADORES DE NÚMEROS
// ============================================================================

/**
 * Sanitiza el monto grupal:
 * - Solo permite dígitos y comas
 * - Formatea con separadores de miles
 * - NO permite decimales
 * @param value Valor de entrada (puede incluir comas)
 * @returns Valor formateado con comas (ej: "10,000") o string vacío
 */
export const sanitizeMonto = (value: string): string => {
  // Solo permitir números y comas (NO punto decimal)
  const cleanValue = value.replace(/[^\d,]/g, '');

  // Obtener solo los dígitos
  const digitsOnly = cleanValue.replace(/,/g, '');

  // Limitar a 9 dígitos
  if (digitsOnly.length > LIMITES.MONTO_GRUPAL.MAX_DIGITOS) {
    return value; // No cambiar si excede el límite
  }

  // Si está vacío, retornar vacío
  if (digitsOnly === '') {
    return '';
  }

  // Convertir a número y formatear con comas
  const numericValue = parseInt(digitsOnly, 10);

  // No permitir ingresar valor superior al máximo permitido
  if (numericValue > LIMITES.MONTO_GRUPAL.MAX) {
    return value;
  }

  return numericValue.toLocaleString('es-MX');
};

/**
 * Convierte un monto formateado (con comas) a número
 * @param montoFormateado Monto con formato (ej: "10,000")
 * @returns Valor numérico o 0 si no es válido
 */
export const montoToNumber = (montoFormateado: string): number => {
  if (!montoFormateado || montoFormateado.trim() === '') {
    return 0;
  }
  const numericValue = parseFloat(montoFormateado.replace(/,/g, ''));
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Convierte un número a formato con comas
 * @param value Valor numérico
 * @returns String formateado (ej: "10,000")
 */
export const numberToMonto = (value: number): string => {
  if (value === 0) {
    return '';
  }
  return value.toLocaleString('es-MX');
};
